import { useState, useEffect, useContext, useMemo, useRef, useCallback, type ComponentProps } from 'react';
import { Polygon } from 'react-leaflet';
import { useNavigate, useParams } from 'react-router-dom';
import SimulationCreationFlow from './SimulationCreationFlow';
import { AuthContext, ViewContext } from 'components/lib';
import { SimulationContext, type SimulationState } from 'contexts/SimulationContext';
import { GeodataContext } from 'contexts/GeodataContext';
import { getSimulationSets, updateSimulationSet } from '../../api/simulationSetApi';
import Name from './shared/Name';
import Solar from './solar/Solar';
import FreeView from './free/FreeView';
import TargetView from './target/TargetView';
import Noise from './noise/Noise';
import Wind from './wind/Wind';
import Shadow from './shadow/Shadow';
import CustomTooltip from 'components/tooltip/CustomTooltip';

import ReviewHeader from '../project-page/components/ReviewHeader';
import ArrowRight from '@untitled-ui/icons-react/build/cjs/ArrowRight';
import Button from '@mui/material/Button';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { UMButton, UMLoader } from '@urbanmetrix/um-kit';
import { clearPendingCountsTowardUsage, getPendingCountsTowardUsage } from '../../services/countsTowardUsage';
import { defaultCoordinateSystemPreference } from 'utils/coordinateSystemManager';
import CadastralMapSection, { ReadOnlyAnalysisAreaCenterMarkerSync } from 'views/add-grid/map/CadastralMapSection';
import MapPreviewModeFrame from 'views/add-grid/map/MapPreviewModeFrame';
import Style from 'views/add-grid/styles/grid.tailwind';
import Settings from 'settings.json';
import { crs2056, INITIAL_GRID_MAP_VIEWS, type GridMapType } from 'views/add-grid/map/mapConfig';
import ProjectCreationShell from 'views/project-creation/ProjectCreationShell';
import ProjectCreationSplitLayout from 'views/project-creation/ProjectCreationSplitLayout';
import usePreviewSlotRectReporter from 'views/project-creation/usePreviewSlotRectReporter';
import ExitDialog from 'components/exit-dialog/ExitDialog';
import { LastAddedItemContext } from 'contexts/LastAddedItemContext';
import { CurrentProjectContext } from 'contexts/CurrentProjectContext';
import { GridContext, type GridParams } from 'contexts/GridContext';
import L from 'leaflet';
import useAddSimulationData from './shared/useAddSimulationData';
import useSimulationTargetsSync from './shared/useSimulationTargetsSync';
import useAddSimulationSubmit from './shared/useAddSimulationSubmit';
import { getRunnableSubSimulations } from './shared/submitSimulationSet';
import { type AddSimulationProps } from './shared/addSimulationShared';
import {
  lv95ToWgs84ForOneCoordinate,
  roundCoordinatesTo8Digits,
  wgs84ToLv95ForOneCoordinate,
} from 'views/add-grid/map/coordinateTransforms';
import {
  alignRectangleDrawing,
  cancelActiveDrawing,
  clearActiveDrawButton,
  enablePolygonRightAngleSnapDrawing,
  highlightActiveDrawButton,
  isDrawButtonActive,
  normalizeRectangle,
  triggerDrawing,
} from 'lib/leafletHelper';
import {
  POLYGON_DRAW_HINT,
  RECTANGLE_DRAW_HINT,
  layerRingToLv95,
  useParcelSelectionOutsideClick,
} from 'views/add-grid/cadastral/cadastralShared';
import { useLegalBorderParcelSelection } from 'views/add-grid/cadastral/useLegalBorderParcelSelection';
import { createNextResidentialArea, getResidentialAreas, type ResidentialArea } from './shadow/shadowResidentialAreas';
import { SHADOW_NOT_DEFINED_MESSAGE } from 'constants/simulation';
import { calculateShadowCircle, doesAreaIntersectShadowCircle } from './shadow/shadowParameters';
import ShadowCoverageDiagram from './shadow/ShadowCoverageDiagram';
import { normalizeMultiPolygon } from 'utils/multiPolygonHelper';
import { extractLargestLv95RingCoordinates, simplifyLv95PolygonCoordinates } from 'utils/geometryHelper';
import './styles/simulation.css';

const SIMULATION_MAP_STYLE = {
  ...Style,
  legalBorder: { ...Style.legalBorder, color: '#c084fc', fillColor: '#c084fc', fillOpacity: 0.14 },
  buildableBorder: { ...Style.buildableBorder, color: '#fdba74', fillColor: '#fdba74', fillOpacity: 0.14 },
};
const SHADOW_MAP_STYLE = {
  ...SIMULATION_MAP_STYLE,
  analysisArea: { ...Style.analysisArea, weight: 1.5, opacity: 0.7 },
};

const SHADOW_NORMAL_AREA_STYLE = {
  color: '#009900',
  fillColor: '#009900',
  fillOpacity: 0.25,
};

const SHADOW_RESIDENTIAL_AREA_STYLE = {
  color: '#3399ff',
  fillColor: '#3399ff',
  fillOpacity: 0.25,
};

const SHADOW_RESIDENTIAL_AREA_OUTSIDE_CIRCLE_MESSAGE =
  'The buildable residential area must include at least a portion within the shadow analysis circle.';

alignRectangleDrawing();
enablePolygonRightAngleSnapDrawing();

type ShadowDrawShape = 'polygon' | 'rectangle';
type ShadowDrawCreatedEvent = { layer: L.Polygon; layerType: string };
type ShadowCoordinate = { x: number; y: number };

const AddSimulation = ({
  reviewMode = false,
  reviewSimulationId = null,
  breadcrumbTitle,
  geodataIdOverride = null,
  gridIdOverride = null,
  projectIdOverride = null,
  simulationSetIdOverride = null,
  onExitOverride,
  onBackOverride,
  disableRun = false,
  isVolumeUsageLoading = false,
  onConfirmVolumeUsage,
  previewLoading = false,
  onUpdatePreview,
  onPreviewStateChange,
  onPreviewSlotRectChange,
  previewRectAnchor = null,
}: AddSimulationProps) => {
  const navigate = useNavigate();
  const params = useParams();
  const viewContext = useContext(ViewContext);
  const geodataIdFromRoute = geodataIdOverride ?? params.geodataId;
  const routeSimulationId = params.simulationId;
  const resolvedReviewSimulationId = reviewSimulationId ?? routeSimulationId;
  const isReadOnlyReview = Boolean(reviewMode || resolvedReviewSimulationId);
  const authContext = useContext(AuthContext);
  const [countsTowardUsage, setCountsTowardUsage] = useState(true);

  const { simulation, setSimulation, cloneSimulationData, setIsCloningSimulation, isCloningSimulation } =
    useContext(SimulationContext);
  const { geodata: geodataDraft, geodataParams, setGeodataParams } = useContext(GeodataContext);
  const { grid: gridDraft } = useContext(GridContext);
  const { currentProject } = useContext(CurrentProjectContext);
  const { setLastAddedItem } = useContext(LastAddedItemContext);

  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [isSimulationNameLoading, setIsSimulationNameLoading] = useState(!isReadOnlyReview && !simulationSetIdOverride);
  const [exitModalOpen, setExitModalOpen] = useState(false);
  const [shadowDefinitionDialogOpen, setShadowDefinitionDialogOpen] = useState(false);
  const [mapType, setMapType] = useState<GridMapType>('cadastral');
  const [mapViews, setMapViews] = useState(INITIAL_GRID_MAP_VIEWS);
  const [isShadowNormalAreaEditing, setIsShadowNormalAreaEditing] = useState(false);
  const [isShadowResidentialAreaEditing, setIsShadowResidentialAreaEditing] = useState(false);
  const [shadowDrawHintText, setShadowDrawHintText] = useState('');
  const [shadowResidentialParcelTarget, setShadowResidentialParcelTarget] = useState<{
    subSimulationId: number;
    areaId: number;
  } | null>(null);
  const [shadowExpandedResidentialAreaId, setShadowExpandedResidentialAreaId] = useState<number | null>(1);
  const [shadowVolumeIndex, setShadowVolumeIndex] = useState(0);
  const [osmDetectionRequestVersion, setOsmDetectionRequestVersion] = useState(0);
  const legalBorderRef = useRef<L.Polygon | null>(null);
  const buildableBorderRef = useRef<L.Polygon | null>(null);
  const analysisAreaRef = useRef<L.Polygon | null>(null);
  const legalBorderInactiveRefs = useRef<L.Polygon[]>([]);
  const buildableBorderInactiveRefs = useRef<L.Polygon[]>([]);
  const analysisAreaInactiveRefs = useRef<L.Polygon[]>([]);
  const isDrawingRef = useRef(false);
  const shadowDrawingSubSimulationIdRef = useRef<number | null>(null);
  const shadowDrawingResidentialTargetRef = useRef<{ subSimulationId: number; areaId: number } | null>(null);
  const shadowNormalAreaRef = useRef<L.Polygon | null>(null);
  const shadowResidentialAreaRef = useRef<L.Polygon | null>(null);
  const shadowCircleRef = useRef<ReturnType<typeof calculateShadowCircle>>(null);
  const previewSlotRef = useRef<HTMLDivElement | null>(null);
  const osmTargetWarningShownRef = useRef(false);
  const isStandaloneSimulationCreationFlow =
    !isReadOnlyReview &&
    !geodataIdOverride &&
    typeof onBackOverride !== 'function' &&
    typeof onExitOverride !== 'function';
  const isProjectCreationFlow = !isReadOnlyReview && Boolean(geodataIdOverride) && typeof onBackOverride === 'function';
  const usesExternalPreview = isProjectCreationFlow && typeof onPreviewStateChange === 'function';
  const showSummaryBackButton = !isReadOnlyReview && !isStandaloneSimulationCreationFlow && !isCloningSimulation;
  const { geodataId, geodata, grid, project, isCloneSessionRef, isTheAreaInSwitzerland, setGeodata } =
    useAddSimulationData({
      currentProject,
      geodataDraft,
      geodataIdFromRoute,
      gridDraft,
      gridIdOverride,
      projectIdOverride,
      simulationSetIdOverride,
      isProjectCreationFlow,
      isReadOnlyReview,
      resolvedReviewSimulationId,
      cloneSimulationData,
      isCloningSimulation,
      setGeodataParams,
      setIsCloningSimulation,
    });
  const { hasDetectionFailed: hasOsmTargetDetectionFailed, isLoadingOsmTargets } = useSimulationTargetsSync({
    osmDetectionRequestVersion,
    shouldDetectOsmTargets: !isCloningSimulation && (!simulationSetIdOverride || osmDetectionRequestVersion > 0),
    geodata,
    geodataParams,
    grid,
    isCloneSessionRef,
    isReadOnlyReview,
    setGeodata,
    setGeodataParams,
    setSimulation,
    simulation,
  });
  useEffect(() => {
    if (simulationSetIdOverride || !geodata?.id) return;

    let cancelled = false;
    setIsSimulationNameLoading(true);
    getSimulationSets({ geodataId: String(geodata.id) })
      .then((simulationSets) => {
        if (cancelled) return;
        const count = Array.isArray(simulationSets)
          ? simulationSets.filter((simulationSet) => String(simulationSet.geodataId) === String(geodata.id)).length
          : 0;
        setSimulation((previous) =>
          isCloningSimulation ? previous : { ...previous, name: `Simulation Set ${count + 1}`, description: '' }
        );
      })
      .catch((error) => console.error('Error loading simulation sets:', error))
      .finally(() => {
        if (!cancelled) setIsSimulationNameLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [geodata?.id, isCloningSimulation, setSimulation, simulationSetIdOverride]);
  const isLandingLoading = isLoadingOsmTargets || isSimulationNameLoading;
  useEffect(() => {
    if (!hasOsmTargetDetectionFailed) {
      osmTargetWarningShownRef.current = false;
      return;
    }

    if (osmTargetWarningShownRef.current) return;
    osmTargetWarningShownRef.current = true;
    viewContext.notification.show(
      'We could not automatically verify whether targets are present. Please check target availability manually.',
      'warning',
      true,
      'toast'
    );
  }, [hasOsmTargetDetectionFailed, viewContext.notification]);
  const { handleCreateSimulationSet, handleSubmitFromSummary, isRunningFromSummary, isSavingFromSummary } =
    useAddSimulationSubmit({
      countsTowardUsage,
      geodataId,
      geodataParams,
      grid,
      isProjectCreationFlow,
      navigate,
      onShadowNotDefined: () => setShadowDefinitionDialogOpen(true),
      project,
      setLastAddedItem,
      setSimulation,
      showNotification: (message) => viewContext.notification.show(message, 'error', true, 'toast'),
      simulation,
      simulationSetId: simulationSetIdOverride,
    });

  useEffect(() => {
    if (!authContext.permission?.support) {
      setCountsTowardUsage(true);
      return;
    }

    setCountsTowardUsage(getPendingCountsTowardUsage());
    clearPendingCountsTowardUsage();
  }, [authContext.permission?.support]);

  const exitTarget = useMemo(() => {
    const projectId = grid?.projectId;
    if (!projectId) return null;
    return `/projects/${projectId}`;
  }, [grid?.projectId]);

  const mapCenter = useMemo<[number, number]>(() => {
    if (project?.coordinates?.lat != null && project?.coordinates?.lng != null) {
      return [Number(project.coordinates.lat), Number(project.coordinates.lng)];
    }
    return [0, 0];
  }, [project?.coordinates?.lat, project?.coordinates?.lng]);

  useEffect(() => {
    if (mapCenter[0] === 0 || mapCenter[1] === 0) {
      return;
    }

    setMapViews((prev) => ({
      cadastral: { ...prev.cadastral, center: mapCenter },
      osm: { ...prev.osm, center: mapCenter },
    }));
  }, [mapCenter]);

  useEffect(() => {
    if (!usesExternalPreview) {
      onPreviewStateChange?.(null);
      return;
    }

    if (showAdvancedSettings) {
      onPreviewStateChange?.(null);
      return;
    }

    if (!grid) {
      return;
    }

    onPreviewStateChange({
      gridId: grid.id,
      isPreviewLoading: previewLoading,
      onUpdatePreview: grid.id && typeof onUpdatePreview === 'function' ? () => onUpdatePreview(String(grid.id)) : null,
      mapCenter,
      gridParams: (grid.params ?? {}) as never,
    });
  }, [
    grid,
    mapCenter,
    onPreviewStateChange,
    onUpdatePreview,
    previewLoading,
    showAdvancedSettings,
    usesExternalPreview,
  ]);

  usePreviewSlotRectReporter({
    enabled: usesExternalPreview,
    element: previewSlotRef.current,
    relativeTo: previewRectAnchor,
    onRectChange: onPreviewSlotRectChange,
  });

  const handleExit = () => {
    if (typeof onExitOverride === 'function') {
      onExitOverride();
      return;
    }

    if (exitTarget) {
      navigate(exitTarget);
      return;
    }
    navigate(-1);
  };

  const handleRunFromSummary = async () => {
    if (isRunningFromSummary || isSavingFromSummary || isLandingLoading || !grid?.id || !geodataId || disableRun) {
      return;
    }

    await handleSubmitFromSummary(true, disableRun);
  };

  const handleRunButtonClick = () =>
    onConfirmVolumeUsage ? onConfirmVolumeUsage(handleRunFromSummary) : handleRunFromSummary();

  const handleSaveFromSummary = async () => {
    if (isRunningFromSummary || isSavingFromSummary || isLandingLoading || !grid?.id || !geodataId) {
      return;
    }

    await handleSubmitFromSummary(false, disableRun);
  };

  const handleShadowDrawRequest = useCallback((shapeType: ShadowDrawShape, subSimulationId: number) => {
    if (isDrawButtonActive(shapeType, 'analysisArea')) {
      setShadowDrawHintText('');
      cancelActiveDrawing({ isDrawingRef });
      shadowDrawingSubSimulationIdRef.current = null;
      shadowDrawingResidentialTargetRef.current = null;
      return;
    }

    shadowDrawingResidentialTargetRef.current = null;
    shadowDrawingSubSimulationIdRef.current = subSimulationId;
    highlightActiveDrawButton(shapeType, 'analysisArea');
    triggerDrawing(shapeType, 'analysisArea');
  }, []);

  const handleShadowResidentialDrawRequest = useCallback(
    (shapeType: ShadowDrawShape, subSimulationId: number, areaId: number) => {
      if (isDrawButtonActive(shapeType, 'analysisArea')) {
        setShadowDrawHintText('');
        cancelActiveDrawing({ isDrawingRef });
        shadowDrawingResidentialTargetRef.current = null;
        return;
      }

      shadowDrawingSubSimulationIdRef.current = null;
      shadowDrawingResidentialTargetRef.current = { subSimulationId, areaId };
      highlightActiveDrawButton(shapeType, 'analysisArea');
      triggerDrawing(shapeType, 'analysisArea');
    },
    []
  );

  const updateShadowNormalArea = useCallback(
    (subSimulationId: number, coordinates: ShadowCoordinate[]) => {
      setSimulation((currentSimulation) => ({
        ...currentSimulation,
        subSimulations: currentSimulation.subSimulations.map((subSimulation) =>
          subSimulation.id === subSimulationId
            ? {
                ...subSimulation,
                params: { ...subSimulation.params, normalBuildableArea: coordinates },
              }
            : subSimulation
        ),
      }));
    },
    [setSimulation]
  );

  const updateShadowResidentialAreaCoordinates = useCallback(
    (subSimulationId: number, areaId: number, coordinates: ShadowCoordinate[]) => {
      if (
        coordinates.length >= 3 &&
        shadowCircleRef.current &&
        !doesAreaIntersectShadowCircle(coordinates, shadowCircleRef.current)
      ) {
        viewContext.notification.show(SHADOW_RESIDENTIAL_AREA_OUTSIDE_CIRCLE_MESSAGE, 'error', true, 'toast');
        return false;
      }
      setSimulation((currentSimulation) => ({
        ...currentSimulation,
        subSimulations: currentSimulation.subSimulations.map((subSimulation) => {
          if (subSimulation.id !== subSimulationId) return subSimulation;
          const currentAreas = getResidentialAreas(subSimulation.params);
          return {
            ...subSimulation,
            params: {
              ...subSimulation.params,
              residentialAreas: currentAreas.map((area) =>
                area.id === areaId ? { ...area, coordinates } : area
              ) as ResidentialArea[],
            },
          };
        }),
      }));
      return true;
    },
    [setSimulation, viewContext.notification]
  );

  const clearShadowResidentialParcelModeUi = useCallback(() => {
    setShadowResidentialParcelTarget(null);
  }, []);

  const applyShadowResidentialParcelFeatures = useCallback(
    (features: GeoJSON.Feature[]) => {
      if (!shadowResidentialParcelTarget) return false;
      const coordinateGroups = features.map((feature) =>
        simplifyLv95PolygonCoordinates(extractLargestLv95RingCoordinates(feature.geometry), { tolerance: 0.08 })
      );
      if (coordinateGroups.some((coordinates) => coordinates.length < 3)) {
        viewContext.notification.show('Unable to use this cadastral parcel.', 'error', true, 'toast');
        return false;
      }
      if (
        shadowCircleRef.current &&
        coordinateGroups.some((coordinates) => !doesAreaIntersectShadowCircle(coordinates, shadowCircleRef.current!))
      ) {
        viewContext.notification.show(SHADOW_RESIDENTIAL_AREA_OUTSIDE_CIRCLE_MESSAGE, 'error', true, 'toast');
        return false;
      }

      setSimulation((currentSimulation) => ({
        ...currentSimulation,
        subSimulations: currentSimulation.subSimulations.map((subSimulation) => {
          if (subSimulation.id !== shadowResidentialParcelTarget.subSimulationId) return subSimulation;
          const currentAreas = getResidentialAreas(subSimulation.params);
          const selectedArea = currentAreas.find((area) => area.id === shadowResidentialParcelTarget.areaId);
          if (!selectedArea) return subSimulation;
          const addedAreas = coordinateGroups.slice(1).reduce<ResidentialArea[]>((areas, coordinates) => {
            const area = createNextResidentialArea([...currentAreas, ...areas]);
            return [...areas, { ...area, coordinates }];
          }, []);
          return {
            ...subSimulation,
            params: {
              ...subSimulation.params,
              residentialAreas: [
                ...currentAreas.map((area) =>
                  area.id === selectedArea.id ? { ...area, coordinates: coordinateGroups[0] } : area
                ),
                ...addedAreas,
              ],
            },
          };
        }),
      }));
      return true;
    },
    [setSimulation, shadowResidentialParcelTarget, viewContext.notification]
  );

  const {
    selectedLegalBorderParcelFeatures: selectedShadowResidentialParcelFeatures,
    clearSelectedLegalBorderParcelFeatures: clearSelectedShadowResidentialParcelFeatures,
    handleLegalBorderParcelSelected: handleShadowResidentialParcelSelected,
    handleSaveLegalBorderParcelSelection: handleSaveShadowResidentialParcelSelection,
  } = useLegalBorderParcelSelection({
    applyLegalBorderFeature: (feature) => applyShadowResidentialParcelFeatures([feature]),
    applyLegalBorderFeatureGroups: applyShadowResidentialParcelFeatures,
    showError: (message) => viewContext.notification.show(message, 'error', true, 'toast'),
    clearParcelModeUi: clearShadowResidentialParcelModeUi,
  });

  const cancelShadowResidentialParcelMode = useCallback(() => {
    clearSelectedShadowResidentialParcelFeatures();
    clearShadowResidentialParcelModeUi();
  }, [clearSelectedShadowResidentialParcelFeatures, clearShadowResidentialParcelModeUi]);

  const interruptShadowResidentialParcelMode = useCallback(() => {
    if (!shadowResidentialParcelTarget) return;
    if (selectedShadowResidentialParcelFeatures.length > 0) {
      handleSaveShadowResidentialParcelSelection();
      return;
    }
    cancelShadowResidentialParcelMode();
  }, [
    cancelShadowResidentialParcelMode,
    handleSaveShadowResidentialParcelSelection,
    selectedShadowResidentialParcelFeatures.length,
    shadowResidentialParcelTarget,
  ]);

  useParcelSelectionOutsideClick(Boolean(shadowResidentialParcelTarget), interruptShadowResidentialParcelMode);

  const toggleShadowResidentialParcelMode = useCallback(
    (subSimulationId: number, areaId: number) => {
      const isActiveTarget =
        shadowResidentialParcelTarget?.subSimulationId === subSimulationId &&
        shadowResidentialParcelTarget.areaId === areaId;
      cancelActiveDrawing({ isDrawingRef });
      setShadowDrawHintText('');
      shadowDrawingSubSimulationIdRef.current = null;
      shadowDrawingResidentialTargetRef.current = null;
      clearActiveDrawButton();
      cancelShadowResidentialParcelMode();
      if (!isActiveTarget) setShadowResidentialParcelTarget({ subSimulationId, areaId });
    },
    [cancelShadowResidentialParcelMode, shadowResidentialParcelTarget]
  );

  const handleShadowAreaCreated = useCallback(
    (event: ShadowDrawCreatedEvent) => {
      const subSimulationId = shadowDrawingSubSimulationIdRef.current;
      const residentialTarget = shadowDrawingResidentialTargetRef.current;
      let layer = event.layer;
      if (event.layerType === 'rectangle' && mapType === 'cadastral') {
        layer = normalizeRectangle(layer);
      }

      const latLngs = layer.getLatLngs() as L.LatLng[] | L.LatLng[][];
      const ring = (Array.isArray(latLngs[0]) ? latLngs[0] : latLngs) as L.LatLng[];
      const coordinates = roundCoordinatesTo8Digits(ring).map(({ lng, lat }) => wgs84ToLv95ForOneCoordinate(lng, lat));
      layer.remove();
      shadowDrawingSubSimulationIdRef.current = null;
      shadowDrawingResidentialTargetRef.current = null;
      isDrawingRef.current = false;
      setShadowDrawHintText('');
      clearActiveDrawButton();

      if (coordinates.length < 3) return;

      if (subSimulationId !== null) {
        updateShadowNormalArea(subSimulationId, coordinates);
        return;
      }

      if (residentialTarget) {
        updateShadowResidentialAreaCoordinates(
          residentialTarget.subSimulationId,
          residentialTarget.areaId,
          coordinates
        );
      }
    },
    [mapType, updateShadowNormalArea, updateShadowResidentialAreaCoordinates]
  );

  const handleDeleteShadowNormalArea = useCallback(
    (subSimulationId: number) => {
      shadowNormalAreaRef.current?.pm?.disable?.();
      setIsShadowNormalAreaEditing(false);
      updateShadowNormalArea(subSimulationId, []);
    },
    [updateShadowNormalArea]
  );

  const handleEditShadowNormalArea = useCallback(
    (subSimulationId: number) => {
      const layer = shadowNormalAreaRef.current;
      if (!layer) return;

      if (isShadowNormalAreaEditing) {
        const coordinates = layerRingToLv95(layer);
        layer.pm.disable();
        setIsShadowNormalAreaEditing(false);
        updateShadowNormalArea(subSimulationId, coordinates);
        return;
      }

      layer.pm.enable({ allowSelfIntersection: false });
      setIsShadowNormalAreaEditing(true);
    },
    [isShadowNormalAreaEditing, updateShadowNormalArea]
  );

  const handleDeleteShadowResidentialArea = useCallback(
    (subSimulationId: number, areaId: number) => {
      shadowResidentialAreaRef.current?.pm?.disable?.();
      setIsShadowResidentialAreaEditing(false);
      updateShadowResidentialAreaCoordinates(subSimulationId, areaId, []);
    },
    [updateShadowResidentialAreaCoordinates]
  );

  const handleEditShadowResidentialArea = useCallback(
    (subSimulationId: number, areaId: number) => {
      const layer = shadowResidentialAreaRef.current;
      if (!layer) return;

      if (isShadowResidentialAreaEditing) {
        const coordinates = layerRingToLv95(layer);
        layer.pm.disable();
        setIsShadowResidentialAreaEditing(false);
        if (!updateShadowResidentialAreaCoordinates(subSimulationId, areaId, coordinates)) {
          const currentArea = getResidentialAreas(
            simulation.subSimulations.find(({ id }) => id === subSimulationId)?.params ?? {}
          ).find(({ id }) => id === areaId);
          const currentCoordinates = Array.isArray(currentArea?.coordinates)
            ? (currentArea.coordinates as ShadowCoordinate[])
            : [];
          layer.setLatLngs(currentCoordinates.map(({ x, y }) => lv95ToWgs84ForOneCoordinate(x, y)));
        }
        return;
      }

      layer.pm.enable({ allowSelfIntersection: false });
      setIsShadowResidentialAreaEditing(true);
    },
    [isShadowResidentialAreaEditing, simulation.subSimulations, updateShadowResidentialAreaCoordinates]
  );

  if (!grid || !geodata) {
    return (
      <div className="review-loader-center">
        <UMLoader />
      </div>
    );
  }

  const reviewHeader = isReadOnlyReview ? (
    <ReviewHeader
      name={simulation?.name}
      description={simulation?.description}
      item={simulation ?? {}}
      onEditDescription={(value) => {
        setSimulation((prev) => ({ ...prev, description: value }));
      }}
      onSaveDescription={(value) => updateSimulationSet(String(resolvedReviewSimulationId), { description: value })}
      onClose={handleExit}
    />
  ) : null;

  const renderSimulationMap = (mapProps: Partial<ComponentProps<typeof CadastralMapSection>> = {}) => (
    <CadastralMapSection
      isReview={false}
      readOnly={true}
      allowAnalysisAreaAngleCopy={true}
      isParcelInteractionDisabled={true}
      mapType={mapType}
      mapViews={mapViews}
      setMapType={setMapType}
      setMapViews={setMapViews}
      mapCenter={mapCenter}
      crs2056={crs2056}
      gridParams={(grid?.params ?? {}) as never}
      Style={SIMULATION_MAP_STYLE}
      legalBorderRef={legalBorderRef}
      buildableBorderRef={buildableBorderRef}
      analysisAreaRef={analysisAreaRef}
      legalBorderInactiveRefs={legalBorderInactiveRefs}
      buildableBorderInactiveRefs={buildableBorderInactiveRefs}
      analysisAreaInactiveRefs={analysisAreaInactiveRefs}
      showInactiveNonAnalysisBorders
      inactiveAnalysisAreaStyle={Style.analysisArea}
      isDrawingRef={isDrawingRef}
      showEditableCoordinatesArea={false}
      AnalysisAreaCenterMarkerSync={ReadOnlyAnalysisAreaCenterMarkerSync}
      cadastralOpacity={Number(Settings[import.meta.env.MODE as keyof typeof Settings].cadastral_opacity)}
      focusedPolygonRef={null}
      coordinateSystem={defaultCoordinateSystemPreference}
      cadastralLayerKey={0}
      {...mapProps}
    />
  );

  const readOnlyMap = renderSimulationMap();
  const shadowAnalysisAreas = normalizeMultiPolygon<ShadowCoordinate>(
    grid.params?.analysisAreas?.length ? grid.params.analysisAreas : grid.params?.analysisArea
  );
  const shadowAnalysisVolumeCount = Math.max(1, shadowAnalysisAreas.length);
  const selectedShadowVolumeIndex = Math.min(shadowVolumeIndex, shadowAnalysisVolumeCount - 1);
  const selectShadowVolume = (nextIndex: number) => {
    cancelShadowResidentialParcelMode();
    shadowNormalAreaRef.current?.pm?.disable?.();
    setIsShadowNormalAreaEditing(false);
    shadowResidentialAreaRef.current?.pm?.disable?.();
    setIsShadowResidentialAreaEditing(false);
    cancelActiveDrawing({ isDrawingRef });
    setShadowDrawHintText('');
    shadowDrawingSubSimulationIdRef.current = null;
    shadowDrawingResidentialTargetRef.current = null;
    clearActiveDrawButton();
    setShadowVolumeIndex((nextIndex + shadowAnalysisVolumeCount) % shadowAnalysisVolumeCount);
  };
  const shadowGridParams = (grid.params ?? {}) as GridParams;
  const shadowLegalBorder =
    normalizeMultiPolygon<ShadowCoordinate>(
      shadowGridParams.legalBorders?.length ? shadowGridParams.legalBorders : shadowGridParams.legalBorder
    )[selectedShadowVolumeIndex] ?? [];
  const shadowBuildableBorder =
    normalizeMultiPolygon<ShadowCoordinate>(
      shadowGridParams.buildableBorders?.length ? shadowGridParams.buildableBorders : shadowGridParams.buildableBorder
    )[selectedShadowVolumeIndex] ?? [];
  const shadowAnalysisArea = shadowAnalysisAreas[selectedShadowVolumeIndex] ?? [];
  const selectedShadowGridParams: GridParams = {
    ...shadowGridParams,
    legalBorder: shadowLegalBorder,
    legalBorders: shadowLegalBorder.length ? [shadowLegalBorder] : [],
    buildableBorder: shadowBuildableBorder,
    buildableBorders: shadowBuildableBorder.length ? [shadowBuildableBorder] : [],
    analysisArea: shadowAnalysisArea,
    analysisAreas: shadowAnalysisArea.length ? [shadowAnalysisArea] : [],
    activeAnalysisAreaIndex: 0,
  };
  const shadowSubSimulation = simulation.subSimulations.find(
    (subSimulation) =>
      subSimulation.simType === 'SHADOW' && Number(subSimulation.params.analysisAreaIndex) === selectedShadowVolumeIndex
  );
  const shadowNormalBuildableArea = shadowSubSimulation?.params.normalBuildableArea as ShadowCoordinate[] | undefined;
  const shadowResidentialAreas: ResidentialArea[] = shadowSubSimulation
    ? getResidentialAreas(shadowSubSimulation.params)
    : [];
  const shadowRule = String(
    simulation.subSimulations.find(({ simType }) => simType === 'SHADOW')?.params.shadowRule ?? 'ZH'
  );
  const shadowCircle = calculateShadowCircle(shadowAnalysisArea, shadowGridParams.gridHeight, shadowRule);
  shadowCircleRef.current = shadowCircle;
  const shadowMap = renderSimulationMap({
    Style: SHADOW_MAP_STYLE,
    readOnly: false,
    allowAnalysisAreaAngleCopy: false,
    isParcelInteractionDisabled: !shadowResidentialParcelTarget,
    gridParams: selectedShadowGridParams,
    showInactiveNonAnalysisBorders: false,
    showInactiveAnalysisAreas: false,
    selectedParcelFeatures: selectedShadowResidentialParcelFeatures,
    onCadastralParcelSelected: (feature) =>
      handleShadowResidentialParcelSelected(feature as unknown as GeoJSON.Feature),
    onCadastralParcelMissClick: interruptShadowResidentialParcelMode,
    onBeforeMapAction: interruptShadowResidentialParcelMode,
    onCreated: handleShadowAreaCreated,
    onDrawStart: (event) => {
      isDrawingRef.current = true;
      setShadowDrawHintText(event.layerType === 'rectangle' ? RECTANGLE_DRAW_HINT : POLYGON_DRAW_HINT);
      highlightActiveDrawButton(event.layerType as ShadowDrawShape, 'analysisArea');
    },
    onDrawStop: () => {
      isDrawingRef.current = false;
      setShadowDrawHintText('');
      clearActiveDrawButton();
    },
    mapContent: (
      <>
        <ShadowCoverageDiagram
          analysisArea={shadowAnalysisArea}
          height={shadowGridParams.gridHeight}
          rule={shadowRule}
        />
        {Array.isArray(shadowNormalBuildableArea) && shadowNormalBuildableArea.length >= 3 ? (
          <Polygon
            key={shadowSubSimulation?.id}
            ref={shadowNormalAreaRef}
            positions={shadowNormalBuildableArea.map(({ x, y }) => lv95ToWgs84ForOneCoordinate(x, y))}
            pathOptions={SHADOW_NORMAL_AREA_STYLE}
          />
        ) : null}
        {shadowResidentialAreas.map((area) => {
          const coordinates = Array.isArray(area.coordinates) ? (area.coordinates as ShadowCoordinate[]) : [];
          if (coordinates.length < 3) return null;
          return (
            <Polygon
              key={`shadow-residential-area-${area.id}`}
              ref={area.id === shadowExpandedResidentialAreaId ? shadowResidentialAreaRef : undefined}
              positions={coordinates.map(({ x, y }) => lv95ToWgs84ForOneCoordinate(x, y))}
              pathOptions={SHADOW_RESIDENTIAL_AREA_STYLE}
            />
          );
        })}
      </>
    ),
  });

  const advancedViews = [
    {
      key: 'solar',
      name: 'Solar',
      title: 'Solar',
      onBack: () => setShowAdvancedSettings(false),
      component: <Solar />,
    },
    {
      key: 'freeView',
      name: 'Free-view',
      onBack: () => setShowAdvancedSettings(false),
      title: (
        <span className="grid-inline-label-with-info">
          <span>Free-view</span>
          <CustomTooltip
            title={
              'In Free-view simulations the open space around a sensor point is quantified in % as a fraction of the visible area or volume around it.'
            }
          />
        </span>
      ),
      component: <FreeView />,
    },
    {
      key: 'targetView',
      name: 'Target-view',
      onBack: () => setShowAdvancedSettings(false),
      title: (
        <span className="grid-inline-label-with-info">
          <span>Target-view</span>
          <CustomTooltip
            title={
              'In Target-view simulations, the visibility of a target is quantified in % as a fraction of the full spherical visibility field around the observer.'
            }
          />
        </span>
      ),
      component: <TargetView geodata={geodata} grid={grid} readOnly={isReadOnlyReview} />,
    },
    {
      key: 'noise',
      name: 'Noise',
      onBack: () => setShowAdvancedSettings(false),
      title: (
        <span className="grid-inline-label-with-info">
          <span>Noise</span>
          <CustomTooltip
            title={
              'Traffic noise results are provided as full-day sound pressure levels produced by the addition of the selected traffic generators. The effect of trees is ignored in our current traffic noise simulations.'
            }
          />
        </span>
      ),
      component: <Noise />,
    },
    {
      key: 'wind',
      name: 'Wind',
      onBack: () => setShowAdvancedSettings(false),
      title: (
        <span className="grid-inline-label-with-info">
          <span>Wind</span>
          <CustomTooltip title={'...'} />
        </span>
      ),
      component: <Wind isTheAreaInSwitzerland={isTheAreaInSwitzerland} />,
    },
    {
      key: 'shadow',
      name: 'Shadow',
      title: 'Shadow',
      hideDefaultHeader: true,
      onBack: () => setShowAdvancedSettings(false),
      fillAvailableSpace: true,
      component: (
        <Shadow
          map={shadowMap}
          drawHintText={shadowDrawHintText}
          isNormalBuildableAreaEditing={isShadowNormalAreaEditing}
          onDrawNormalBuildableArea={handleShadowDrawRequest}
          onSelectNormalBuildableArea={(source, subSimulationId) =>
            updateShadowNormalArea(
              subSimulationId,
              source === 'property'
                ? shadowLegalBorder
                : source === 'buildable'
                  ? shadowBuildableBorder
                  : shadowAnalysisArea
            )
          }
          onDeleteNormalBuildableArea={handleDeleteShadowNormalArea}
          onEditNormalBuildableArea={handleEditShadowNormalArea}
          normalBuildableAreaRef={shadowNormalAreaRef}
          isResidentialAreaEditing={isShadowResidentialAreaEditing}
          onDrawResidentialArea={handleShadowResidentialDrawRequest}
          isResidentialParcelMode={shadowResidentialParcelTarget !== null}
          isResidentialParcelModeUnavailable={mapType === 'osm'}
          residentialParcelSelectionCount={selectedShadowResidentialParcelFeatures.length}
          onToggleResidentialParcelMode={toggleShadowResidentialParcelMode}
          onSaveResidentialParcelSelection={handleSaveShadowResidentialParcelSelection}
          onCancelResidentialParcelMode={cancelShadowResidentialParcelMode}
          onUpdateResidentialArea={updateShadowResidentialAreaCoordinates}
          onDeleteResidentialArea={handleDeleteShadowResidentialArea}
          onEditResidentialArea={handleEditShadowResidentialArea}
          residentialAreaRef={shadowResidentialAreaRef}
          expandedResidentialAreaId={shadowExpandedResidentialAreaId}
          onExpandResidentialArea={setShadowExpandedResidentialAreaId}
          hasPropertyArea={shadowLegalBorder.length >= 3}
          hasBuildableArea={shadowBuildableBorder.length >= 3}
          hasAnalysisArea={shadowAnalysisArea.length >= 3}
          analysisVolumeCount={shadowAnalysisVolumeCount}
          selectedVolumeIndex={selectedShadowVolumeIndex}
          onSelectVolume={selectShadowVolume}
        />
      ),
    },
  ];

  const viewMapping: Record<string, keyof SimulationState> = {
    solar: 'solarEnabled',
    freeView: 'freeViewEnabled',
    targetView: 'targetViewEnabled',
    noise: 'noiseEnabled',
    wind: 'windEnabled',
    shadow: 'shadowEnabled',
  };

  const filteredAdvancedViews = advancedViews.filter((view) => {
    const flag = viewMapping[view.key];
    if (simulation.useStandardSettings) {
      return true;
    }

    return flag ? simulation[flag] : false;
  });
  const openAdvancedSettings = (viewKey?: string) => {
    const viewIndex = viewKey ? filteredAdvancedViews.findIndex((view) => view.key === viewKey) : 0;
    if (viewIndex < 0) return;

    if (simulation.useStandardSettings) {
      setSimulation((prevSimulation) => ({
        ...prevSimulation,
        useStandardSettings: false,
        solarEnabled: true,
        freeViewEnabled: true,
        targetViewEnabled: true,
        noiseEnabled: true,
        windEnabled: true,
      }));
    }

    window.location.hash = String(viewIndex + 1);
    setShowAdvancedSettings(true);
  };
  const isGeodataGathered = geodata?.progressStatus === 'SUCCEEDED';
  const hasAnySelectedSummarySimulation =
    getRunnableSubSimulations(
      simulation,
      grid as { params?: Record<string, unknown> | null } | null,
      geodataParams?.lengthMid,
      Array.isArray(geodataParams?.targets) ? geodataParams.targets : []
    ).length > 0;

  return (
    <>
      <ProjectCreationShell
        hidden={showAdvancedSettings}
        title={breadcrumbTitle ?? (isReadOnlyReview ? 'Simulation Review' : 'Add Simulations')}
        onClose={isReadOnlyReview ? undefined : () => setExitModalOpen(true)}
        hideDefaultHeader={isReadOnlyReview}
        customHeader={isReadOnlyReview ? reviewHeader : null}
        footerLeft={
          showSummaryBackButton ? (
            <Button
              variant="contained"
              startIcon={<ChevronLeftIcon />}
              onClick={onBackOverride ?? handleExit}
              className="onboarding-prev-button"
            >
              Back
            </Button>
          ) : null
        }
        footerRight={
          isReadOnlyReview ? null : (
            <div className="flex items-center gap-1">
              {!isStandaloneSimulationCreationFlow ? (
                <UMButton
                  text={isSavingFromSummary ? 'SAVING...' : 'SAVE'}
                  onClick={handleSaveFromSummary}
                  icon={ArrowRight}
                  customClassName="onboarding-finish-button"
                  disabled={
                    isRunningFromSummary || isSavingFromSummary || isLandingLoading || !hasAnySelectedSummarySimulation
                  }
                  loading={isSavingFromSummary}
                />
              ) : null}
              <UMButton
                text={isRunningFromSummary ? 'RUNNING...' : 'RUN'}
                onClick={
                  isStandaloneSimulationCreationFlow
                    ? () => handleCreateSimulationSet(isGeodataGathered)
                    : handleRunButtonClick
                }
                icon={ArrowRight}
                customClassName="onboarding-finish-button onboarding-finish-button--primary"
                disabled={
                  isRunningFromSummary ||
                  isSavingFromSummary ||
                  isLandingLoading ||
                  isVolumeUsageLoading ||
                  disableRun ||
                  (isStandaloneSimulationCreationFlow && !hasAnySelectedSummarySimulation)
                }
                loading={isVolumeUsageLoading || isRunningFromSummary || previewLoading}
              />
            </div>
          )
        }
      >
        <div className="grid-analysis-step">
          <ProjectCreationSplitLayout
            className={`grid-settings-step ${isReadOnlyReview ? 'grid-settings-step--review' : ''}`.trim()}
            leftClassName="grid-settings-step__sidebar"
            rightClassName={`grid-settings-step__preview grid-settings-step__preview--project${
              usesExternalPreview ? ' grid-settings-step__preview--external-host' : ''
            }`}
            left={
              isLandingLoading ? (
                <div className="simulation-summary__loader">
                  <UMLoader />
                </div>
              ) : (
                <Name
                  grid={grid}
                  mode="summary"
                  isReviewMode={isReadOnlyReview}
                  onOpenAdvancedSettings={openAdvancedSettings}
                  onTargetViewEnabled={() => setOsmDetectionRequestVersion((version) => version + 1)}
                />
              )
            }
            right={
              usesExternalPreview ? (
                <div ref={previewSlotRef} className="grid-settings-step__preview-slot" />
              ) : (
                <MapPreviewModeFrame
                  gridId={grid?.id}
                  isPreviewLoading={previewLoading}
                  onUpdatePreview={
                    grid?.id && typeof onUpdatePreview === 'function' ? () => onUpdatePreview(String(grid.id)) : null
                  }
                >
                  {readOnlyMap}
                </MapPreviewModeFrame>
              )
            }
          />
        </div>
        {!isReadOnlyReview && (
          <>
            <ExitDialog
              open={exitModalOpen}
              onClose={() => setExitModalOpen(false)}
              mode="confirm"
              onCancel={() => {
                if (isRunningFromSummary) return;
                setExitModalOpen(false);
              }}
              onConfirm={() => {
                if (isRunningFromSummary) return;
                setExitModalOpen(false);
                handleExit();
              }}
            />
            <ExitDialog
              open={shadowDefinitionDialogOpen}
              onClose={() => setShadowDefinitionDialogOpen(false)}
              mode="confirm"
              title={SHADOW_NOT_DEFINED_MESSAGE}
              width={460}
              cancelLabel="Cancel"
              confirmLabel="Set Shadow"
              onCancel={() => setShadowDefinitionDialogOpen(false)}
              onConfirm={() => {
                setShadowDefinitionDialogOpen(false);
                openAdvancedSettings('shadow');
              }}
            />
          </>
        )}
      </ProjectCreationShell>
      {showAdvancedSettings && geodata && grid && (
        <SimulationCreationFlow
          views={filteredAdvancedViews}
          geodataId={geodataId ?? undefined}
          isGeodataGathered={isGeodataGathered}
          gridData={grid}
          projectData={project}
          preserveDraftState={isProjectCreationFlow}
          countsTowardUsage={countsTowardUsage}
          readOnly={isReadOnlyReview}
          hideForwardNavigation
          onExit={handleExit}
        />
      )}
    </>
  );
};

export default AddSimulation;
